import { StandardError, TimeoutError } from "@core-ts/StandardError"

import Long from "long"
import { log } from "./Contexts/Context"

// Browser compatible: This file must be compatible with a browser

export function throwExpression(errorMessage: string): never {
    throw new StandardError(errorMessage)
}

export async function delay(timeoutMs: number, signal?: AbortSignal): Promise<void> {
    await new Promise(f => {
        const timeout = setTimeout(f, timeoutMs)

        if (signal) {
            signal.addEventListener(
                "abort",
                () => {
                    clearTimeout(timeout)
                    // biome-ignore lint/nursery/noFloatingPromises: <this is not actually a floating promise>
                    f(signal.reason)
                },
                { once: true },
            )
        }
    })
}

export function asyncInterval(callback: () => Promise<void>, signal: AbortSignal, intervalMs: number): void {
    // This function is meant to launch a loop like a sub-process, therefor we do not wait for it and void it instead.
    void (async () => {
        while (!signal.aborted) {
            const startTime = Date.now()
            // We might or might not want this to swallow errors by default,
            // but it does right now as that is what we want for the only feature using asyncInterval
            try {
                await callback()
            } catch (e) {}
            const endTime = Date.now()

            if (signal.aborted) break

            const elapsedTime = endTime - startTime
            const timeToWait = intervalMs - elapsedTime

            if (timeToWait > 0) {
                await delay(timeToWait, signal)
            }
        }
    })()
}

export async function waitUntil(timeout: Date | null): Promise<void> {
    if (timeout === null) return

    const now = new Date()
    if (now >= timeout) return
    const waitMs = timeout.getTime() - now.getTime()

    await delay(waitMs)
}

export async function isPromiseResolved(promise: Promise<unknown>): Promise<boolean> {
    // Promise.race will always resolve the first promise in the array first. The second promise will be resolved imedietly, returning "pending".
    //So the first promise will get a chance first to be resolved, and if it is not, the second "control" promise will be resolved will resolve and the function return false
    return (
        (await Promise.race([
            promise,
            new Promise(resolve => {
                resolve("pending")
            }),
        ])) !== "pending"
    )
}

export function longToNum(num: string | number | Long): number {
    switch (typeof num) {
        case "string": {
            return parseInt(num)
        }
        case "number": {
            return num
        }
        default: {
            return num.toNumber()
        }
    }
}

export function longToNumAllowNull(num: string | number | Long | null | undefined): number | null {
    if (num === undefined || num === null) return null
    switch (typeof num) {
        case "string": {
            return parseInt(num)
        }
        case "number": {
            return num
        }
        default: {
            return num.toNumber()
        }
    }
}

export function castAsLong(num: string | number | Long): Long {
    switch (typeof num) {
        case "string": {
            return Long.fromString(num)
        }
        case "number": {
            return Long.fromNumber(num)
        }
        default: {
            return num
        }
    }
}

export function castAsNumber(num: string | number | Long): number {
    switch (typeof num) {
        case "string": {
            return parseInt(num)
        }
        case "number": {
            return num
        }
        default: {
            return num.toNumber()
        }
    }
}

export function parseBool(sBool: string | null | undefined): boolean {
    return !!sBool && sBool.toLowerCase() === "true"
}

// Don't use when you need performance, for that use sortAndRemoveDuplicatesU32InPlace
export function sortAndRemoveDuplicates(input: number[]): number[] {
    const inputSet = Array.from(new Set(input).values()) // Remove duplicates by converting to and from a Set

    return inputSet.sort((a, b) => a - b)
}

export function sortAndRemoveDuplicatesU32InPlace(input: Uint32Array): Uint32Array {
    const n = input.length
    if (n <= 1) return input

    input.sort()

    let uniques = 1
    for (let i = 1; i < n; i++) {
        if (input[i] !== input[i - 1]) uniques++
    }

    if (uniques === n) {
        return input
    }

    const out = new Uint32Array(uniques)
    out[0] = input[0]

    let j = 1
    for (let i = 1; i < n; i++) {
        const v = input[i]
        if (v !== input[i - 1]) out[j++] = v
    }

    return out
}

// Utility function that creates a timeout promise
export function timeout(ms: number): Promise<never> {
    return new Promise((_, reject) => {
        setTimeout(() => reject(new TimeoutError()), ms)
    })
}

// The main function that runs the lambda with a timeout
export async function runWithTimeout<X>(fn: () => Promise<X>, timeoutMs: number): Promise<X> {
    // Race the lambda function against a timeout
    return await Promise.race([fn(), timeout(timeoutMs)])
}

// Build a URL, in a similar way that path.join works for file paths
export function joinUrl(base: string | URL, ...paths: string[]): string {
    let baseUrl: URL

    if (typeof base === "string") {
        baseUrl = new URL(base)
    } else {
        baseUrl = base
    }

    paths.unshift(baseUrl.pathname)

    return new URL(paths.join("/"), baseUrl).toString()
}

export function mergeObjects<T>(target: T, ...sources: T[]): T {
    if (!sources.length) return target
    const source = sources.shift()

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            const nestedSource = source[key]
            if (isObject(nestedSource)) {
                if (!target[key]) Object.assign(target, { [key]: {} })

                const nestedTarget = target[key]

                if (isObject(nestedTarget)) {
                    mergeObjects(nestedTarget, nestedSource)
                }
            } else {
                Object.assign(target, { [key]: source[key] })
            }
        }
    }

    return mergeObjects(target, ...sources)
}

export function isObject(item: unknown): item is Record<string, unknown> {
    // You would think you could only return the if statement here, but that does not work for some reason
    if (item && typeof item === "object" && !Array.isArray(item)) {
        return true
    }
    return false
}

export function isValidEmail(email?: string | null): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

    if (!email) return false

    return emailRegex.test(email)
}
